import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { restaurantCategoriesService } from '../../services/restaurantCategoriesService';
import { uploadService } from '../../services/uploadService';
import { RestaurantsService } from '../../services/restaurantsService';
import {
  RestaurantCategoryResponse,
  CreateRestaurantCategoryRequest,
  UpdateRestaurantCategoryRequest,
} from '../../types/restaurantCategories';
import { RestaurantResponse } from '../../types/restaurants';
import '../MainLayout.css';
import './Page.css';

const restaurantsService = new RestaurantsService();

const RestaurantCategories: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<RestaurantCategoryResponse[]>([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<RestaurantCategoryResponse | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageId, setImageId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [priority, setPriority] = useState('0');
  const [isActive, setIsActive] = useState(true);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');

  // Drag and drop state
  const [reorderedCategories, setReorderedCategories] = useState<RestaurantCategoryResponse[]>([]);
  const [hasReordered, setHasReordered] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Restaurant attachment state
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [attachingToCategoryId, setAttachingToCategoryId] = useState<number | null>(null);
  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [restaurants, setRestaurants] = useState<RestaurantResponse[]>([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [attachingRestaurant, setAttachingRestaurant] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    // Initialize reordered categories when categories are loaded
    setReorderedCategories(categories);
    setHasReordered(false);
  }, [categories]);

  const fetchCategories = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await restaurantCategoriesService.getAllRestaurantCategories();

      if (response.success && response.data) {
        setCategories(response.data);
      } else {
        setError(response.error || 'Failed to fetch categories');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setTitle('');
    setDescription('');
    setImageId('');
    setImageUrl('');
    setImagePreviewUrl('');
    setPriority(categories.length.toString());
    setIsActive(true);
    setImageFile(null);
    setShowModal(true);
  };

  const openEditModal = (category: RestaurantCategoryResponse) => {
    setEditingCategory(category);
    setTitle(category.title);
    setDescription(category.description);
    setImageId(category.imageId);
    setImageUrl(category.imageUrl);
    setImagePreviewUrl('');
    setPriority(category.priority.toString());
    setIsActive(category.isActive);
    setImageFile(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setError('');
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('banners.invalidImageType')); // Reusing banner translation key
      return;
    }

    // Validate file size (10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      setError(t('banners.imageTooLarge')); // Reusing banner translation key
      return;
    }

    setError('');

    try {
      // Store the file for later upload
      setImageFile(file);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreviewUrl(previewUrl);

      // Clear existing image ID if editing
      if (!editingCategory) {
        setImageId('');
      }
    } catch (error) {
      setError('Failed to load image preview');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!title.trim()) {
      setError(t('restaurantCategories.nameRequired'));
      return;
    }

    // Check if we have an image (either existing imageId or new imageFile)
    if (!imageId && !imageFile) {
      setError(t('restaurantCategories.imageRequired'));
      return;
    }

    const priorityNum = parseInt(priority);
    if (isNaN(priorityNum) || priorityNum < 0) {
      setError(t('restaurantCategories.invalidPriority'));
      return;
    }

    // Upload new image if selected
    let finalImageId = imageId;
    if (imageFile) {
      setModalLoading(true);

      try {
        // Compress image using browser-image-compression
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          fileType: 'image/jpeg' as const,
        };
        const compressedFile = await imageCompression(imageFile, options);

        // Upload image
        const uploadResponse = await uploadService.uploadImage(compressedFile);

        if (uploadResponse.success && uploadResponse.data) {
          finalImageId = uploadResponse.data.id;
        } else {
          setError(uploadResponse.error || t('banners.uploadFailed')); // Reusing banner translation key
          setModalLoading(false);
          return;
        }
      } catch (error) {
        setError(t('banners.uploadFailed')); // Reusing banner translation key
        setModalLoading(false);
        return;
      }
    }

    if (!modalLoading) {
      setModalLoading(true);
    }

    try {
      if (editingCategory) {
        // Update category
        const request: UpdateRestaurantCategoryRequest = {
          title: title.trim(),
          description: description.trim(),
          imageId: finalImageId,
          priority: priorityNum,
          isActive,
        };

        const response = await restaurantCategoriesService.updateRestaurantCategory(editingCategory.id, request);

        if (response.success) {
          setSuccessMessage(t('restaurantCategories.categoryUpdated'));
          await fetchCategories();
          closeModal();
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError(response.error || 'Failed to update category');
        }
      } else {
        // Create category
        const request: CreateRestaurantCategoryRequest = {
          title: title.trim(),
          description: description.trim(),
          imageId: finalImageId,
          priority: priorityNum,
          isActive,
        };

        const response = await restaurantCategoriesService.createRestaurantCategory(request);

        if (response.success) {
          setSuccessMessage(t('restaurantCategories.categoryCreated'));
          await fetchCategories();
          closeModal();
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError(response.error || 'Failed to create category');
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (categoryId: number) => {
    if (!window.confirm(t('restaurantCategories.confirmDelete'))) {
      return;
    }

    setError('');

    try {
      const response = await restaurantCategoriesService.deleteRestaurantCategory(categoryId);

      if (response.success) {
        setSuccessMessage(t('restaurantCategories.categoryDeleted'));
        await fetchCategories();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to delete category');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === index) return;

    const newCategories = [...reorderedCategories];
    const draggedItem = newCategories[draggedIndex];

    // Remove dragged item
    newCategories.splice(draggedIndex, 1);

    // Insert at new position
    newCategories.splice(index, 0, draggedItem);

    setReorderedCategories(newCategories);
    setDraggedIndex(index);
    setHasReordered(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const savePriorities = async () => {
    setModalLoading(true);
    setError('');

    try {
      const request = {
        categories: reorderedCategories.map((category, index) => ({
          categoryId: category.id,
          priority: index,
        })),
      };

      const response = await restaurantCategoriesService.updateCategoryPriorities(request);

      if (response.success) {
        setSuccessMessage(t('restaurantCategories.prioritiesUpdated'));
        setHasReordered(false);
        await fetchCategories();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to update priorities');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const cancelReorder = () => {
    setReorderedCategories(categories);
    setHasReordered(false);
  };

  const openAttachModal = (categoryId: number) => {
    setAttachingToCategoryId(categoryId);
    setRestaurantSearch('');
    setRestaurants([]);
    setShowAttachModal(true);
  };

  const closeAttachModal = () => {
    setShowAttachModal(false);
    setAttachingToCategoryId(null);
    setRestaurantSearch('');
    setRestaurants([]);
  };

  const searchRestaurants = async (query: string) => {
    if (query.length < 2) {
      setRestaurants([]);
      return;
    }

    setLoadingRestaurants(true);

    try {
      const response = await restaurantsService.getRestaurants({
        searchQuery: query,
        page: 0,
        pageSize: 20,
        applicationStatus: null,
      });

      if (response.success && response.data) {
        setRestaurants(response.data);
      }
    } catch (error) {
      console.error('Failed to search restaurants:', error);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const handleAttachRestaurant = async (restaurantId: number) => {
    if (!attachingToCategoryId) return;

    setAttachingRestaurant(true);
    setError('');

    try {
      const response = await restaurantCategoriesService.attachCategoryToRestaurant(
        attachingToCategoryId,
        restaurantId
      );

      if (response.success) {
        setSuccessMessage('Restaurant attached to category successfully');
        await fetchCategories();
        closeAttachModal();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to attach restaurant');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setAttachingRestaurant(false);
    }
  };

  const handleDetachRestaurant = async (categoryId: number, restaurantId: number, restaurantName: string) => {
    if (!window.confirm(`Are you sure you want to remove "${restaurantName}" from this category?`)) {
      return;
    }

    setError('');

    try {
      const response = await restaurantCategoriesService.detachCategoryFromRestaurant(categoryId, restaurantId);

      if (response.success) {
        setSuccessMessage('Restaurant removed from category successfully');
        await fetchCategories();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to remove restaurant');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>{t('restaurantCategories.title')}</h1>
          <p>{t('restaurantCategories.subtitle')}</p>
        </div>
        <div className="page-content">
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            {t('restaurantCategories.loading')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="action-button secondary"
          >
            ← {t('restaurantCategories.backToSettings')}
          </button>
          <div>
            <h1>{t('restaurantCategories.title')}</h1>
            <p>{t('restaurantCategories.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#d4edda',
              color: '#155724',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              marginBottom: '1.5rem',
            }}
          >
            {successMessage}
          </div>
        )}

        <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem' }}>
          <button
            type="button"
            onClick={openCreateModal}
            style={{
              padding: '0.6rem 1.2rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: 500,
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
          >
            + {t('restaurantCategories.createNewCategory')}
          </button>

          {hasReordered && (
            <>
              <button
                type="button"
                onClick={savePriorities}
                disabled={modalLoading}
                style={{
                  padding: '0.6rem 1.2rem',
                  backgroundColor: modalLoading ? '#ccc' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: modalLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#0056b3')}
                onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#007bff')}
              >
                {modalLoading ? t('restaurantCategories.savingPriorities') : t('restaurantCategories.saveReorder')}
              </button>
              <button
                type="button"
                onClick={cancelReorder}
                disabled={modalLoading}
                style={{
                  padding: '0.6rem 1.2rem',
                  backgroundColor: modalLoading ? '#e0e0e0' : '#f8f9fa',
                  color: modalLoading ? '#999' : '#333',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  cursor: modalLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#e2e6ea')}
                onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#f8f9fa')}
              >
                {t('common.cancel')}
              </button>
            </>
          )}
        </div>

        {categories.length === 0 ? (
          <div className="detail-card full-width">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <p>{t('restaurantCategories.noCategories')}</p>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {reorderedCategories.map((category, index) => (
              <div
                key={category.id}
                className="detail-card full-width"
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  cursor: 'grab',
                  transition: 'background-color 0.2s',
                  backgroundColor: draggedIndex === index ? '#f0f8ff' : 'white',
                }}
              >
                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
                  {/* Category Image */}
                  <div
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {category.imageUrl ? (
                      <img
                        src={category.imageUrl}
                        alt={category.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <span style={{ color: '#999', fontSize: '0.85rem' }}>{t('common.noImage')}</span>
                    )}
                  </div>

                  {/* Category Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ margin: 0 }}>{category.title}</h3>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: category.isActive ? '#d4edda' : '#f8d7da',
                          color: category.isActive ? '#155724' : '#721c24',
                          border: `1px solid ${category.isActive ? '#c3e6cb' : '#f5c6cb'}`,
                        }}
                      >
                        {category.isActive ? t('banners.active') : t('banners.inactive')}
                      </span>
                    </div>
                    <p style={{ color: '#666', marginBottom: '0.75rem' }}>{category.description}</p>

                    <div style={{ display: 'flex', gap: '2rem', marginBottom: '0.75rem' }}>
                      <div>
                        <strong>{t('restaurantCategories.priority')}:</strong> {index}
                      </div>
                      <div>
                        <strong>Attached Restaurants:</strong> {category.attachedRestaurants.length}
                      </div>
                    </div>

                    {/* Attached Restaurants with Detach */}
                    {category.attachedRestaurants.length > 0 && (
                      <div style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {category.attachedRestaurants.map((r) => (
                            <div
                              key={r.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.375rem 0.625rem',
                                backgroundColor: '#e7f3ff',
                                border: '1px solid #b3d9ff',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                              }}
                            >
                              <span>{r.name}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDetachRestaurant(category.id, r.id, r.name);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#dc3545',
                                  cursor: 'pointer',
                                  fontSize: '1rem',
                                  padding: '0',
                                  lineHeight: '1',
                                  fontWeight: 'bold',
                                }}
                                title="Remove restaurant"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Attach Restaurant Button */}
                    <div style={{ marginTop: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openAttachModal(category.id);
                        }}
                        style={{
                          padding: '0.375rem 0.75rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                      >
                        + Attach Restaurant
                      </button>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => openEditModal(category)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#007bff'}
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(category.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
            overflowY: 'auto',
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '95vh',
              overflow: 'auto',
              padding: '2.5rem',
              margin: '1rem auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '1.5rem' }}>
              {editingCategory ? t('restaurantCategories.editCategory') : t('restaurantCategories.createCategory')}
            </h2>

            {error && (
              <div className="error-message" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('restaurantCategories.categoryName')} *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('restaurantCategories.categoryNamePlaceholder')}
                  className="search-input"
                  required
                  disabled={modalLoading}
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('banners.bannerDescription')}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="search-input"
                  disabled={modalLoading}
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {/* Is Active */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    disabled={modalLoading}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: 500 }}>{t('banners.isActive')}</span>
                </label>
                <small style={{ display: 'block', marginTop: '0.25rem', marginLeft: '26px', color: '#666' }}>
                  {t('banners.isActiveHint')}
                </small>
              </div>

              {/* Image Upload */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('restaurantCategories.categoryImage')} *
                </label>
                <small style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
                  {t('restaurantCategories.categoryImageHint')}
                </small>

                {(imagePreviewUrl || imageUrl) && (
                  <div
                    style={{
                      marginBottom: '1rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      maxWidth: '200px',
                    }}
                  >
                    <img
                      src={imagePreviewUrl || imageUrl}
                      alt="Preview"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <label
                    htmlFor="category-image-upload"
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: modalLoading ? '#ccc' : '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: modalLoading ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transition: 'background-color 0.2s',
                      display: 'inline-block',
                    }}
                    onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#138496')}
                    onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#17a2b8')}
                  >
                    {imagePreviewUrl || imageUrl
                      ? t('banners.changeImage') // Reusing banner translation key
                      : t('banners.uploadImage')} {/* Reusing banner translation key */}
                  </label>
                  <input
                    id="category-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    disabled={modalLoading}
                    style={{ display: 'none' }}
                  />

                  {(imagePreviewUrl || imageUrl) && (
                    <button
                      type="button"
                      onClick={() => {
                        setImageId('');
                        setImageUrl('');
                        setImagePreviewUrl('');
                        setImageFile(null);
                      }}
                      disabled={modalLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: modalLoading ? '#ccc' : '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: modalLoading ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#c82333')}
                      onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#dc3545')}
                    >
                      {t('banners.removeImage')} {/* Reusing banner translation key */}
                    </button>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button
                  type="submit"
                  disabled={modalLoading}
                  style={{
                    padding: '0.6rem 1.5rem',
                    backgroundColor: modalLoading ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: modalLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#218838')}
                  onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#28a745')}
                >
                  {modalLoading
                    ? (imageFile ? t('banners.uploadingImage') + '...' : t('common.saving')) // Reusing banner translation key
                    : editingCategory
                    ? t('restaurantCategories.updateCategory')
                    : t('restaurantCategories.createCategory')}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={modalLoading}
                  style={{
                    padding: '0.6rem 1.5rem',
                    backgroundColor: modalLoading ? '#e0e0e0' : '#f8f9fa',
                    color: modalLoading ? '#999' : '#333',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    cursor: modalLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#e2e6ea')}
                  onMouseLeave={(e) => !modalLoading && (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Attach Restaurant Modal */}
      {showAttachModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: '1rem',
          }}
          onClick={closeAttachModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: '2rem',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '1.5rem' }}>Attach Restaurant to Category</h2>

            {error && (
              <div className="error-message" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Search Restaurant
              </label>
              <input
                type="text"
                value={restaurantSearch}
                onChange={(e) => {
                  setRestaurantSearch(e.target.value);
                  searchRestaurants(e.target.value);
                }}
                placeholder="Type restaurant name to search..."
                className="search-input"
                disabled={attachingRestaurant}
              />
              {loadingRestaurants && (
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                  Searching...
                </small>
              )}
            </div>

            {restaurants.length > 0 && (
              <div
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}
              >
                {restaurants.map((restaurant) => (
                  <div
                    key={restaurant.id}
                    onClick={() => handleAttachRestaurant(restaurant.id)}
                    style={{
                      padding: '0.75rem',
                      cursor: attachingRestaurant ? 'not-allowed' : 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background-color 0.2s',
                      opacity: attachingRestaurant ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => !attachingRestaurant && (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                    onMouseLeave={(e) => !attachingRestaurant && (e.currentTarget.style.backgroundColor = 'white')}
                  >
                    <div style={{ fontWeight: 500 }}>{restaurant.name}</div>
                    <small style={{ color: '#666' }}>ID: {restaurant.id}</small>
                  </div>
                ))}
              </div>
            )}

            {restaurantSearch.length >= 2 && !loadingRestaurants && restaurants.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                No restaurants found
              </div>
            )}

            <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
              <button
                type="button"
                onClick={closeAttachModal}
                disabled={attachingRestaurant}
                style={{
                  padding: '0.6rem 1.5rem',
                  backgroundColor: attachingRestaurant ? '#e0e0e0' : '#f8f9fa',
                  color: attachingRestaurant ? '#999' : '#333',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  cursor: attachingRestaurant ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantCategories;
